import * as React from 'react';

// Custom useMutationObserver hook
const useMutationObserver = (
  ref: React.MutableRefObject<Element | null>,
  callback: (mutations: MutationRecord[]) => void,
  options: MutationObserverInit,
) => {
  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new MutationObserver(callback);
    observer.observe(ref.current, options);

    return () => {
      observer.disconnect();
    };
  }, [ref, callback, options]);
};

// Function to remove ramp- prefix from class names
const cleanClassName = (className: string): string | undefined => {
  if (typeof className !== 'string') {
    return undefined;
  }

  return className
    .split(' ')
    .map((cls) => (cls.startsWith('ramp-') ? cls.replace('ramp-', '') : cls))
    .join(' ');
};

// Function to clean all ramp- prefixes from an element and its children
const cleanRampPrefixes = (element: Element) => {
  if (element.className) {
    const originalClass = element.className;
    const cleanedClass = cleanClassName(originalClass);

    if (cleanedClass && originalClass !== cleanedClass) {
      element.className = cleanedClass;
    }
  }

  // Clean all children
  Array.from(element.children).forEach((child) => cleanRampPrefixes(child));
};

// Custom hook for ramp prefix cleaning with mutation observer
export const useRampPrefixCleaner = <T extends HTMLElement>() => {
  const elementRef = React.useRef<T | null>(null);

  // Ref callback to clean ramp prefixes
  const handleRef = React.useCallback((element: T | null) => {
    if (element) {
      // Clean any existing ramp- prefixes (server-side issue)
      cleanRampPrefixes(element);

      // Additional cleanup after a short delay to catch any late-loading elements
      setTimeout(() => {
        cleanRampPrefixes(element);
      }, 100);
    }
  }, []);

  // Monitor DOM mutations and clean ramp prefixes
  useMutationObserver(
    elementRef,
    (mutations: MutationRecord[]) => {
      mutations.forEach((mutation: MutationRecord) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class'
        ) {
          const target = mutation.target as Element;
          const currentClass = target.className;

          // Check if className has ramp- prefix and clean it
          if (currentClass.includes('ramp-')) {
            const cleanedClass = cleanClassName(currentClass);
            console.log(
              '[Ramp Prefix Debug] Ramp prefix detected via mutation:',
              {
                timestamp: Date.now(),
                url: window.location.href,
                environment: process.env.NEXT_PUBLIC_INFRASTRUCTURE_ENV,
                currentClass,
                cleanedClass,
                tagName: target.tagName,
                targetElement: target,
                mutationType: mutation.type,
                attributeName: mutation.attributeName,
                stackTrace: new Error().stack,
              },
            );

            if (cleanedClass) {
              target.className = cleanedClass;
            }
          }
        }

        // Monitor for new child additions and clean them
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node: Node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              // Clean any ramp- prefixes from newly added elements
              cleanRampPrefixes(element);

              console.log('[Ramp Prefix Debug] New child node added:', {
                timestamp: Date.now(),
                tagName: element.tagName,
                className: element.className,
                parent: mutation.target,
              });
            }
          });
        }
      });
    },
    {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true, // Monitor all descendants
    },
  );

  // Combined ref callback that handles both internal ref and cleaning
  const combinedRef = React.useCallback(
    (node: T | null, externalRef?: React.Ref<T>) => {
      elementRef.current = node;
      handleRef(node);

      if (typeof externalRef === 'function') {
        externalRef(node);
      } else if (externalRef && 'current' in externalRef) {
        (externalRef as React.MutableRefObject<T | null>).current = node;
      }
    },
    [handleRef],
  );

  return { combinedRef };
};
